// Thread context (D8/D9) — the viewer-relative context line + lane pill + view
// link rendered at the top of every transaction-born thread (Design Bible D8,
// D9; frames 5e, 5g). PURE module: no React, fully unit-tested against the D9
// wording matrix so the byte-exact prefixes can never silently drift.
//
// Data source (be-core V5): channel.data._tp_custom_context.origin (the canonical
// thread ORIGIN) + .reference ({type, id, title}). Origin derivation reuses the
// shared laneMapping.deriveOrigin so BE and FE classify identically; a thread
// with no transactional origin (DM / lawyer / player / …) renders NO context
// line (identity-only header) — never a faked one (Bible V5 fallback).
//
// D9 is VIEWER-RELATIVE: the origin OWNER and the COUNTERPART see different
// wording for the same thread. Owner/counterpart is resolved from the origin +
// the account markers already on the channel (be-core's origin_owner_account_id
// when present — preferred; else club_account_id/agent_account_id/poster; else a
// role-perspective fallback). See resolveIsOwner.

import { ORIGIN, deriveOrigin, PERSPECTIVE } from "../../utils/laneMapping";

// Lane / origin pills (CANON-SPECS §A "Lane / origin pills (D9)").
export const LANE_PILL = {
  [ORIGIN.CLUB_REQUEST]: { label: "Club Request", bg: "#EEF0FF", fg: "#444CE7" },
  [ORIGIN.AGENT_REQUEST]: { label: "Agent Request", bg: "#F0F6FF", fg: "#1466D8" },
  [ORIGIN.SEARCH_REQUEST]: { label: "Search Request", bg: "#E5F5F6", fg: "#0E7C86" },
  [ORIGIN.SCOUT_REQUEST]: { label: "Scout Request", bg: "#FDF1E7", fg: "#B4550A" },
  [ORIGIN.LAST_MINUTE]: { label: "Last Minute", bg: "#FDECEA", fg: "#C2362B" },
};

// The five transaction origins that carry a D9 context line. DM and the DM-only
// role origins (lawyer/player/organiser/friendly) + staff_application render no
// context card here (identity-only, Bible D20 / V5 fallback).
export const CONTEXT_ORIGINS = new Set(Object.keys(LANE_PILL));

// Which SIDE owns each origin (fallback owner resolution when no explicit
// owner-account stamp is present). club-owned: club posted/searched/holds the
// scout request; agent-owned: agent posted the request OR is the Last-Minute
// listing's poster.
const OWNER_SIDE = {
  [ORIGIN.CLUB_REQUEST]: PERSPECTIVE.CLUB,
  [ORIGIN.SEARCH_REQUEST]: PERSPECTIVE.CLUB,
  [ORIGIN.SCOUT_REQUEST]: PERSPECTIVE.CLUB,
  [ORIGIN.AGENT_REQUEST]: PERSPECTIVE.AGENT,
  [ORIGIN.LAST_MINUTE]: PERSPECTIVE.AGENT,
};

// resolveIsOwner — is the current viewer the OWNER of this thread's origin?
// Returns true/false, or null when it genuinely cannot be determined (a rare
// agent↔agent thread with no owner stamp — see the be-core ask for
// origin_owner_account_id). Precedence:
//   1. explicit be-core stamp _tp_custom_context.origin_owner_account_id (exact);
//   2. account markers already on the channel: poster_account_id (agent board
//      response) / club_account_id / agent_account_id vs the owner SIDE;
//   3. role perspective vs the owner side (correct for every club↔agent origin;
//      only the unstamped agent↔agent case degrades to "both look like owner",
//      which #1 fixes once be-core lands the stamp).
export const resolveIsOwner = ({
  channelData,
  viewerAccountId,
  origin,
  perspective,
}) => {
  const ctx = (channelData && channelData._tp_custom_context) || {};
  const vid = viewerAccountId != null ? String(viewerAccountId) : null;

  // 1) Explicit owner stamp (preferred — purely viewer-relative, no guessing).
  if (ctx.origin_owner_account_id != null) {
    return vid != null && vid === String(ctx.origin_owner_account_id);
  }

  const ownerSide = OWNER_SIDE[origin] || null;

  // 2) Account markers already stamped on the channel.
  let ownerAcc = null;
  const poster = channelData?.poster_account_id;
  if (
    poster != null &&
    (origin === ORIGIN.AGENT_REQUEST || origin === ORIGIN.LAST_MINUTE)
  ) {
    // create_agent_response_channel stamps poster/responder; the poster owns the
    // origin (their posted request / their listing).
    ownerAcc = poster;
  } else if (ownerSide === PERSPECTIVE.CLUB && ctx.club_account_id != null) {
    ownerAcc = ctx.club_account_id;
  } else if (ownerSide === PERSPECTIVE.AGENT && ctx.agent_account_id != null) {
    ownerAcc = ctx.agent_account_id;
  }
  if (ownerAcc != null && vid != null) return vid === String(ownerAcc);

  // 3) Role-perspective fallback — ONLY reliable on a club↔agent thread, where
  // the two parties have OPPOSITE perspectives. On an unstamped agent↔agent
  // thread both sides share the agent perspective, so guessing "owner" would
  // mislabel one side (and dangle a view link they don't own); return null there
  // → the card shows the safe counterpart wording with no link until be-core
  // stamps origin_owner_account_id. (nichts erfinden.)
  const ctxType = ctx.tp_context || channelData?.tp_context || null;
  const isClubAgent =
    ctxType === "club_agent" ||
    ctx.club_account_id != null ||
    ctx.agent_account_id != null;
  if (ownerSide && perspective && isClubAgent) return perspective === ownerSide;
  return null;
};

// View-link (owner only, lane-specific per D9): "View request" for club/agent/
// search, "View listing" for Last Minute, NONE for Scout Request and DM. The
// deep-link target is resolved from the reference {type,id}; only rendered when a
// route + id resolve. (Route choices flagged in the FE-P2 report for review.)
const VIEW_ROUTE = {
  listing: (id) => ({ label: "View listing", path: `/last-minute?listing=${id}` }),
  tender: (id) => ({ label: "View request", path: `/club/tenders/${id}/pitches` }),
  agent_tender: (id) => ({ label: "View request", path: `/agent-tenders/${id}/pitch` }),
  search_request: (id) => ({ label: "View request", path: `/network/trusted-agent/${id}` }),
  // scout_recommendation: intentionally no view link (D9).
};

export const viewLinkFor = (reference) => {
  const ref = reference || {};
  const builder = VIEW_ROUTE[ref.type];
  if (!builder || ref.id == null) return null;
  return builder(ref.id);
};

// Compose the D9 context LINE. The PREFIX strings are the byte-exact GESETZ
// (Bible §1 D9 / frame 5g); the variable tail is filled from the best available
// datum — reference.title (a spec) or the other party's identity — never
// invented. `otherName` = the other participant's person name; `otherClubName` =
// their club name when the counterpart is a club.
export const buildContextLine = ({
  origin,
  isOwner,
  refTitle,
  refPlayerName,
  refScoutTitle,
  otherName,
  otherClubName,
}) => {
  const title = refTitle ? String(refTitle).trim() : "";
  const withTail = (prefix, tail) => (tail ? `${prefix} · ${tail}` : prefix);
  const clubIdentity = otherClubName || otherName || "";

  if (isOwner) {
    switch (origin) {
      case ORIGIN.CLUB_REQUEST:
        return withTail("Response to your Club Request", title);
      case ORIGIN.AGENT_REQUEST:
        return withTail("Response to your Agent Request", title);
      case ORIGIN.SEARCH_REQUEST:
        return withTail("Response to your Search Request", title);
      case ORIGIN.SCOUT_REQUEST:
        // Owner (club) sees "recommendation by <agent>"; the agent is the other
        // party (their name is the reliable identity for this slot).
        return withTail(
          "About your Scout Request",
          otherName ? `recommendation by ${otherName}` : "recommendation"
        );
      case ORIGIN.LAST_MINUTE:
        return withTail("Response to your listing", title);
      default:
        return null;
    }
  }

  // Counterpart (the other side of the origin).
  switch (origin) {
    case ORIGIN.CLUB_REQUEST:
      // "via Club Request · <club> — <spec>". reference.title already carries the
      // position (and, for a pitch, the club) so it stands in for the spec.
      return withTail("via Club Request", title || clubIdentity);
    case ORIGIN.AGENT_REQUEST:
      // Owner is an agent → the other party's person name.
      return withTail("via Agent Request", otherName || title);
    case ORIGIN.SEARCH_REQUEST:
      // Owner is a club → the club identity.
      return withTail("via Search Request", clubIdentity || title);
    case ORIGIN.SCOUT_REQUEST: {
      // 5g: "About your recommendation — <player> · <scout request>". be-core
      // enriched the reference (32c0114) with player_name + scout_request_title;
      // fall back to reference.title (which now carries the player) for legacy
      // rows, and render the fixed prefix only when nothing is stampable.
      const player = refPlayerName || title;
      if (!player) return "About your recommendation";
      const head = `About your recommendation — ${player}`;
      return refScoutTitle ? `${head} · ${refScoutTitle}` : head;
    }
    case ORIGIN.LAST_MINUTE:
      return withTail("via Last Minute", title);
    default:
      return null;
  }
};

// describeThreadContext — the single call the ThreadContextCard makes. Returns
// the full descriptor { origin, pill, line, isOwner, view } or null when the
// thread carries no transactional origin (identity-only header).
export const describeThreadContext = ({
  channelData,
  viewerAccountId,
  perspective = PERSPECTIVE.AGENT,
  otherName = null,
  otherClubName = null,
}) => {
  const origin = deriveOrigin(channelData || {});
  if (!CONTEXT_ORIGINS.has(origin)) return null;

  const reference = channelData?._tp_custom_context?.reference || {};
  const isOwner = resolveIsOwner({
    channelData,
    viewerAccountId,
    origin,
    perspective,
  });
  const line = buildContextLine({
    origin,
    isOwner: isOwner === true,
    refTitle: reference.title || null,
    // Enriched scout reference (be-core 32c0114) → full 5g counterpart wording.
    refPlayerName: reference.player_name || null,
    refScoutTitle: reference.scout_request_title || null,
    otherName,
    otherClubName,
  });
  // View link is OWNER-only (the counterpart cannot open the owner's request
  // page); Scout Request never has one (D9).
  const view = isOwner === true ? viewLinkFor(reference) : null;

  return { origin, pill: LANE_PILL[origin] || null, line, isOwner, view };
};

export default describeThreadContext;
